import { message } from 'antd'
import { useSignedPdfFetcher } from 'pages/reporting/shared/eSignature/useSignedPdfFetcher'
import { toPdfBlob } from 'pages/reporting/shared/eSignature/utils/signReport'
import { prepareClientCopy } from 'pages/reporting/shared/eSignature/utils/stampSignature'
import { useCallback } from 'react'
import { ReportESignatureRecord } from 'types'
import { downloadBlob } from 'utils/download'

/**
 * `client` is the final report as it goes out: cover signatures only.
 * `audit` keeps the signature certificate page for QA/Compliance.
 */
export type SignedPdfCopy = 'client' | 'audit'

/**
 * Downloads a report's latest signed PDF. Usable anywhere the entry's
 * signature records are at hand, so the report does not have to be opened first.
 */
export function useSignedPdfDownload() {
  const { fetchVerifiedSignedPdf } = useSignedPdfFetcher()

  return useCallback(
    async (
      metadataEntryId: string,
      records: readonly ReportESignatureRecord[],
      copy: SignedPdfCopy
    ) => {
      const lastRecord = records.at(-1)
      if (!lastRecord) return
      try {
        const signed = await fetchVerifiedSignedPdf(metadataEntryId, lastRecord)
        const bytes =
          copy === 'client' ? await prepareClientCopy(signed) : signed
        downloadBlob(
          toPdfBlob(bytes),
          `report-${metadataEntryId}-signed${copy === 'audit' ? '-audit' : ''}.pdf`
        )
      } catch (error) {
        message.error(
          error instanceof Error
            ? error.message
            : 'Failed to download signed PDF'
        )
      }
    },
    [fetchVerifiedSignedPdf]
  )
}
